import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";

// A quantity stepper you can also type into.
//
// The steppers used to be the only way to set a quantity, which made the bulk
// offers painful to take up: reaching 100 copies meant a hundred clicks. The
// field accepts a typed number, and the buttons still work for small changes.
//
// While typing, the box is allowed to be empty or mid-number — forcing it back
// to 1 on every keystroke makes it impossible to replace "85" with "100". The
// value is only corrected when the field loses focus.

const SIZES = {
  sm: { btn: "px-2 py-1", icon: "w-3 h-3", input: "w-10 py-1 text-xs" },
  md: { btn: "px-2.5 py-1.5", icon: "w-3 h-3", input: "w-14 py-1.5 text-sm" },
  lg: { btn: "px-3.5 py-2.5", icon: "w-3.5 h-3.5", input: "w-16 py-2.5 text-sm" },
};

export default function QuantityInput({
  value,
  onChange,
  min = 1,
  max = 9999,
  size = "md",
  testId,
  label = "Quantity",
}) {
  const s = SIZES[size] || SIZES.md;
  const [draft, setDraft] = useState(String(value));

  // Follow the value when it changes elsewhere (Order Now, another tab).
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = (n) => {
    const clamped = Math.min(max, Math.max(min, n));
    onChange(clamped);
    return clamped;
  };

  const handleTyping = (raw) => {
    const digits = raw.replace(/[^\d]/g, "");
    setDraft(digits);
    if (digits === "") return; // let them clear the box before retyping
    commit(parseInt(digits, 10));
  };

  const handleBlur = () => {
    if (draft === "" || parseInt(draft, 10) < min) {
      setDraft(String(commit(min)));
      return;
    }
    setDraft(String(commit(parseInt(draft, 10))));
  };

  return (
    <div className="inline-flex items-center border border-[#6e5022]/30 w-fit">
      <button
        type="button"
        onClick={() => commit(value - 1)}
        disabled={value <= min}
        aria-label={`Decrease ${label.toLowerCase()}`}
        data-testid={testId ? `${testId}-decrease` : undefined}
        className={`${s.btn} hover:bg-[#F4F1EA] disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <Minus className={s.icon} strokeWidth={1.5} />
      </button>

      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => handleTyping(e.target.value)}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        aria-label={label}
        data-testid={testId}
        className={`${s.input} text-center bg-transparent outline-none focus:bg-[#F4F1EA] border-x border-[#6e5022]/20`}
      />

      <button
        type="button"
        onClick={() => commit(value + 1)}
        disabled={value >= max}
        aria-label={`Increase ${label.toLowerCase()}`}
        data-testid={testId ? `${testId}-increase` : undefined}
        className={`${s.btn} hover:bg-[#F4F1EA] disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <Plus className={s.icon} strokeWidth={1.5} />
      </button>
    </div>
  );
}
